import { useEffect, useMemo, useRef, useState } from 'react';

export interface Pagination<T> {
  page: number;
  setPage: (page: number) => void;
  pageCount: number;
  pageItems: T[];
  total: number;
  /** 1-based index of the first item on the page (0 when empty). */
  from: number;
  to: number;
}

/**
 * Client-side paging. The page is clamped when the list shrinks; pass `resetKey`
 * (e.g. the active filter/search) to return to page 1 whenever it changes.
 */
export function usePagination<T>(items: T[], pageSize = 10, resetKey?: unknown): Pagination<T> {
  const [rawPage, setRawPage] = useState(1);
  const firstKey = useRef(resetKey);
  useEffect(() => {
    if (firstKey.current !== resetKey) { firstKey.current = resetKey; setRawPage(1); }
  }, [resetKey]);
  const total = items.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const page = Math.min(Math.max(1, rawPage), pageCount);
  const pageItems = useMemo(() => items.slice((page - 1) * pageSize, page * pageSize), [items, page, pageSize]);
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  return { page, setPage: setRawPage, pageCount, pageItems, total, from, to };
}

export default usePagination;
